import React from "react";
import { formatDecimal, formatText } from "../../utils/formatters";

// (2026-05-17) Rediseño Sirius v2. Solo presentación/markup/clases.
// HERO header (sirius-gradient + mesh) con avatar de iniciales, secciones
// tituladas con icono + divisor, y filas de campo con micro-label + icono.
// Toda la lógica/bindings/condicionales de `message` se conservan idénticos.
function ModalClients({ isOpen, onClose, message }) {
  if (!isOpen) return null;

  const Initials = (() => {
    const src = (message.nombre_empresa || "").trim();
    if (!src) return null;
    const parts = src.split(/\s+/).filter(Boolean);
    const letters = (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
    return letters.toUpperCase() || null;
  })();

  const Item = ({ label, icon, children, full }) => (
    <div className={full ? "w-full" : "w-full sm:w-1/2 lg:w-1/3"}>
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4 h-full transition hover:border-sirius-200 hover:shadow-sirius">
        <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-500 mb-2">
          <i className={`fas ${icon} text-sirius-500 text-[12px]`} aria-hidden="true" />
          {label}
        </h3>
        <div className="text-sm font-medium text-ink-900 break-words leading-relaxed">{children}</div>
      </div>
    </div>
  );

  const SectionTitle = ({ icon, children }) => (
    <div className="flex items-center gap-3 mt-9 mb-4">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sirius-50 text-sirius-600 shrink-0">
        <i className={`fas ${icon} text-sm`} aria-hidden="true" />
      </span>
      <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-ink-500 whitespace-nowrap">
        {children}
      </h3>
      <span className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-[1100px] max-h-[90vh] flex flex-col overflow-hidden animate-sirius-fade-up">
        {/* HERO HEADER */}
        <div className="relative shrink-0 px-6 md:px-8 py-7 bg-sirius-night overflow-hidden">
          {/* (2026-05-17) Fondo robusto: gradiente oscuro sólido + mesh como
              capa overlay (no apilar dos bg-image en el mismo nodo: el mesh
              tiene zonas transparentes y dejaba la cabecera lavada/sin
              contraste para el texto blanco). Patrón igual a Login/404. */}
          <div className="pointer-events-none absolute inset-0 bg-sirius-mesh opacity-60" />
          <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-spark-500/30 blur-[110px]" />
          <div className="pointer-events-none absolute -bottom-24 -left-10 w-72 h-72 rounded-full bg-sirius-500/25 blur-[110px]" />
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-4 right-4 z-10 inline-flex items-center justify-center w-9 h-9 rounded-full text-white/80 bg-white/10 hover:bg-white/20 hover:text-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <i className="fas fa-xmark text-lg" aria-hidden="true" />
          </button>

          <div className="relative z-10 flex items-center gap-5 pr-12">
            <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl glass-panel flex items-center justify-center text-white font-display font-bold text-2xl md:text-3xl shadow-spark">
              {Initials ? (
                Initials
              ) : (
                <i className="fas fa-building text-2xl md:text-3xl" aria-hidden="true" />
              )}
            </div>

            <div className="min-w-0">
              <h2 className="font-display font-bold text-white text-xl md:text-2xl leading-tight break-words">
                {message.nombre_empresa || "—"}
              </h2>
              <p className="text-sm text-white/70 mt-0.5 break-words">
                {message.denominacion_comercial || "—"}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1.5 bg-white/10 text-white/90 py-1 px-3 rounded-full text-xs font-semibold">
                  <i className="fas fa-id-card" aria-hidden="true" />
                  {message.rif || "—"}
                </span>
                {message.activo == true ? (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-circle-check" aria-hidden="true" />
                    ACTIVO
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 bg-rose-500 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-circle-xmark" aria-hidden="true" />
                    INACTIVO
                  </span>
                )}
                {message.es_emisor && (
                  <span className="inline-flex items-center gap-1.5 bg-white/15 text-white py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-paper-plane" aria-hidden="true" />
                    EMISOR
                  </span>
                )}
                {message.es_tercero && (
                  <span className="inline-flex items-center gap-1.5 bg-amber-400 text-amber-950 py-1 px-3 rounded-full text-xs font-bold">
                    <i className="fas fa-handshake" aria-hidden="true" />
                    TERCERO
                  </span>
                )}
                {/* (2026-07-22 Viajes) La marca `es_agencia_viajes` NO se muestra
                    aquí por decisión del cliente: las píldoras de este encabezado
                    indican el TIPO de empresa (EMISOR / TERCERO), mientras que ser
                    agencia de viajes es un ATRIBUTO. Se representa más abajo con la
                    convención canónica de booleanos del proyecto (círculo verde/rojo
                    + Si/No), igual que el resto de los flags. */}
              </div>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-8 py-6">
          {/* Información de contacto */}
          <SectionTitle icon="fa-address-card">Información de contacto</SectionTitle>
          <div className="flex flex-wrap gap-3">
            <Item label="RIF" icon="fa-id-card">{message.rif || "—"}</Item>
            <Item label="Razón Social" icon="fa-building">{message.nombre_empresa || "—"}</Item>
            <Item label="Denominación comercial" icon="fa-store">{message.denominacion_comercial || "—"}</Item>
            <Item label="Teléfono" icon="fa-phone">{message.telefono || "—"}</Item>
            <Item label="Correo electrónico" icon="fa-envelope">{message.email || "—"}</Item>
            <Item label="Dirección" icon="fa-location-dot" full>{message.direccion || "—"}</Item>
          </div>

          {/* Ubicación */}
          <SectionTitle icon="fa-map-location-dot">Ubicación</SectionTitle>
          <div className="flex flex-wrap gap-3">
            <Item label="Región" icon="fa-map">{formatText(message.region || '') || "—"}</Item>
            <Item label="Estado" icon="fa-map-pin">{formatText(message.estado || '') || "—"}</Item>
            <Item label="Zona" icon="fa-location-crosshairs">{formatText(message.zona || '') || "—"}</Item>
            <Item label="Tipo de contribuyente" icon="fa-file-invoice">{formatText(message.tipo_contribuyente?.nombre || '') || "—"}</Item>
            <Item label="Condición" icon="fa-circle-info">
              {message.activo == true ? (
                <span className="inline-block bg-emerald-500 text-white py-1 px-3 rounded-full text-xs font-bold">ACTIVO</span>
              ) : (
                <span className="inline-block bg-rose-500 text-white py-1 px-3 rounded-full text-xs font-bold">INACTIVO</span>
              )}
            </Item>
            {/* (2026-07-22 Viajes) Booleano en formato canónico del proyecto:
                círculo verde/rojo + Si/No (mismo estilo que las columnas de
                estado de los listados). */}
            <Item label="Agencia de viajes" icon="fa-plane-departure">
              {message.es_agencia_viajes == true ? (
                <span className="whitespace-nowrap">
                  <i className="fas fa-circle text-emerald-500 mr-2" aria-hidden="true" />
                  Si
                </span>
              ) : (
                <span className="whitespace-nowrap">
                  <i className="fas fa-circle text-red-500 mr-2" aria-hidden="true" />
                  No
                </span>
              )}
            </Item>
          </div>

          {/* Configuración de moneda */}
          <SectionTitle icon="fa-coins">Configuración de moneda</SectionTitle>
          <div className="flex flex-wrap gap-3">
            <Item label="Moneda designada" icon="fa-coins">
              {message.moneda_designada
                ? `${message.moneda_designada.codigo} — ${message.moneda_designada.nombre}`
                : '—'}
            </Item>
            <Item label="Moneda extranjera" icon="fa-coins">
              {message.moneda_extranjera
                ? `${message.moneda_extranjera.codigo} — ${message.moneda_extranjera.nombre}`
                : '—'}
              {message.has_facturas && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200/70 py-0.5 px-2 rounded-full">
                  <i className="fas fa-lock" aria-hidden="true" />
                  Bloqueado (con facturas)
                </span>
              )}
            </Item>
            {/* (2026-06-10) Regla de costo (Compras) — cómo se actualiza el
                costo de los productos al registrar compras. */}
            <Item label="Regla de costo (Compras)" icon="fa-coins">
              <span className={
                "inline-block py-1 px-3 rounded-full text-xs font-bold " +
                (message.regla_costo === 'promedio_ponderado'
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-slate-200 text-slate-700")
              }>
                {message.regla_costo === 'promedio_ponderado' ? 'PROMEDIO PONDERADO' : 'ÚLTIMO COSTO'}
              </span>
              <span className="ml-2 text-[10px] text-slate-500">
                {message.regla_costo === 'promedio_ponderado'
                  ? '(promedia el costo con cada compra)'
                  : '(sobrescribe con la última compra)'}
              </span>
            </Item>
          </div>

          {/* Facturación de terceros — visible solo si aplica */}
          {(message.es_emisor || message.es_tercero) && (
            <>
              <SectionTitle icon="fa-handshake">Facturación de terceros</SectionTitle>
              <div className="flex flex-wrap gap-3">
                <Item label="Tipo" icon="fa-tags">
                  {message.es_emisor && (
                    <span className="inline-block bg-twilight-indigo-100 text-twilight-indigo-700 py-1 px-3 rounded-full text-xs font-bold">EMISOR</span>
                  )}
                  {message.es_tercero && (
                    <span className="inline-block bg-amber-100 text-amber-700 py-1 px-3 rounded-full text-xs font-bold ml-2">TERCERO</span>
                  )}
                </Item>
                {/* (2026-05-20) Regla de facturacion del emisor */}
                {message.es_emisor && message.regla_facturacion && (
                  <Item label="Regla de facturación" icon="fa-list-check">
                    <span className={
                      "inline-block py-1 px-3 rounded-full text-xs font-bold " +
                      (message.regla_facturacion === 'Combinada'
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-200 text-slate-700")
                    }>
                      {message.regla_facturacion.toUpperCase()}
                    </span>
                    <span className="ml-2 text-[10px] text-slate-500">
                      {message.regla_facturacion === 'Combinada'
                        ? '(comisión como línea adicional)'
                        : '(comisión en factura separada)'}
                    </span>
                  </Item>
                )}
                {message.es_tercero && (
                  <Item label="Comisión de servicio" icon="fa-percent">
                    {message.comision_servicio != null
                      ? `${formatDecimal(message.comision_servicio)} %`
                      : '—'}
                  </Item>
                )}
                {message.es_tercero && message.padre && (
                  <Item label="Emisor vinculado" icon="fa-link" full>
                    {formatText(message.padre.nombre_empresa)} ({message.padre.rif})
                  </Item>
                )}
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl shadow-sirius transition focus:outline-none focus-visible:ring-2 focus-visible:ring-twilight-indigo-400"
          >
            <i className="fas fa-xmark" aria-hidden="true" />
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalClients;
